<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import IconEyeOutline from '@/components/icon/IconEyeOutline.vue'
import SiteAdminService, { isPaywallSiteUnconfigured } from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import { formatMoney, TX_STATUS_LABELS, TX_STATUS_VARIANTS, paidPurchaseCount, productTitle } from '@/Utils/siteTransactions'
import SiteRevenueChart from '@/components/Sites/SiteRevenueChart.vue'
import SitesPlanLockBadge from '@/components/Sites/SitesPlanLockBadge.vue'
import BroadcastGivingSummaryCard from '@/components/Sites/Broadcast/BroadcastGivingSummaryCard.vue'
import { isBroadcast as isBroadcastSite } from '@/components/Sites/siteTemplates'

// Overview = status, not settings: live state + primary action, a setup
// checklist computed from the site's data, the 30-day revenue trend and the
// latest purchases. Name, address, Stripe and deletion live under
// Settings › General; the detailed revenue cards on Monetize › Transactions.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})

const store = useStore()
const customDomainLocked = computed(() => store.getters['Sites/customDomainLocked'])
const { notify } = useNotification()
const admin = useSiteAdmin(() => props.siteId)
const draft = useSiteDraft(() => props.siteId)
// Address not routed by the edge yet (polled by the shell): Preview / View site wait for it.
const readiness = useSiteHostReadiness(() => props.siteId)
const addressPending = computed(() => readiness.gated.value)
const lockedTitle = readiness.lockedTitle

const site = computed(() => store.getters['Sites/siteById'](props.siteId) || props.site)
const status = computed(() => site.value?.status)
// Broadcast template: the giving card (native donations summary) joins the left column.
const isBroadcast = computed(() => isBroadcastSite(site.value))
const isEnabled = computed(() => status.value === 'active')
const isDraftSite = computed(() => status.value === 'draft' || status.value === 'creating')
const publishFailed = computed(() => site.value?.publish?.status === 'failed')
const publishSkipped = computed(() => site.value?.publish?.status === 'skipped')
// Paywall linkage (viewer accounts + purchases) is mirrored on every publish;
// a failed mirror is retried by the same "Retry publish" action.
const paywallSyncFailed = computed(() => site.value?.paywallSync?.status === 'failed')
const hasDraft = computed(() => draft.hasDraft.value)
const draftCount = computed(() => draft.draftCount.value)
const publishing = computed(() => draft.state.value.publishing)
const previewing = computed(() => draft.state.value.previewing)

const togglingStatus = ref(false)
const onStatusToggle = async (value) => {
	if (togglingStatus.value) return
	togglingStatus.value = true
	try {
		await store.dispatch('Sites/updateSite', { id: props.siteId, payload: { status: value ? 'active' : 'disabled' } })
		notify({ group: 'success', text: value ? 'Site enabled.' : 'Site disabled.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not change the site status.' })
	}
	togglingStatus.value = false
}

const publish = async () => {
	try {
		const res = await draft.publish()
		const updated = res?.site
		if (updated?.publish?.status === 'failed') notify({ group: 'error', text: updated.publish.error || 'Publishing to the edge failed.' })
		else if (updated?.paywallSync?.status === 'failed') notify({ group: 'error', text: updated.paywallSync.error || 'Paywall link failed.' })
	} catch {
		// toast already shown by useSiteDraft
	}
}
const openPreview = () => {
	if (addressPending.value) return
	draft.openPreview()
}
const openSite = () => {
	if (addressPending.value) return
	if (site.value?.url) window.open(site.value.url, '_blank', 'noopener')
}

// --- setup checklist ---------------------------------------------------------
// Four of the five steps are read off async loads (content, plans, Stripe,
// settings) that start out empty, so the card is a neutral skeleton until all
// of them have answered: rendering the derived state meanwhile made a fully
// set-up site read "Set up your site · 1 of 5 done · 2 required steps left"
// with action buttons on every refresh (Trello N2t21lvo). A step whose load
// failed is "unknown" — listed with a Retry, never counted as left to do, and
// the header never claims "Setup complete" while a required step is unknown.
const checklistLoading = ref(true)
const loadError = (key) => admin.errors.value?.[key] || null
const stripeConnected = computed(() => admin.monetization.value?.stripe?.connected === true)
// GET /plans also carries the Stripe state, so one of the two answering is enough.
const stripeUnknown = computed(() => !admin.monetization.value?.stripe && !!loadError('monetization'))
const hasContent = computed(() => admin.content.value.length > 0)
const contentUnknown = computed(() => !!loadError('content'))
const hasPlan = computed(() => admin.sellablePlans.value.some((p) => p.active !== false))
// Not linked to a paywall site yet (503 `paywall_site_unconfigured`) = it cannot have plans: known, not unknown.
const planUnknown = computed(() => !!loadError('plans') && !isPaywallSiteUnconfigured(loadError('plans')))
// "Customised" = anything beyond what castr seeded at creation.
const themeCustomized = computed(() => {
	const s = admin.settings.value
	if (!s) return false
	const seeded = new Set(admin.settingsMeta.value?.seeded || [])
	const own = (key) => s[key] !== undefined && s[key] !== null && s[key] !== '' && !seeded.has(key)
	return own('websiteLogo') || own('fontFamily') || (s.colors && Object.keys(s.colors).length > 0 && !seeded.has('colors'))
})
const themeUnknown = computed(() => !admin.settings.value && !!loadError('settings'))
const isPublished = computed(() => !isDraftSite.value)

// one GET /settings feeds both the checklist and the shared draft indicator
const loadSettings = () => admin.loadSettings().then(() => { if (admin.settingsMeta.value) draft.applySettingsResponse(admin.settingsMeta.value) })

const checklist = computed(() => [
	{ id: 'stripe', label: 'Connect Stripe', hint: 'Only needed to sell paid plans.', done: stripeConnected.value, unknown: stripeUnknown.value, optional: true, to: { name: 'SiteGeneral', params: { siteId: props.siteId } }, action: 'Connect', retry: admin.loadMonetization },
	{ id: 'content', label: 'Add content', hint: 'A live stream or a video from your Castr account.', done: hasContent.value, unknown: contentUnknown.value, to: { name: 'SiteContent', params: { siteId: props.siteId } }, action: 'Add content', retry: admin.loadContent },
	{ id: 'plan', label: 'Add a plan', hint: 'What viewers can buy — skip it for a free site.', done: hasPlan.value, unknown: planUnknown.value, optional: true, to: { name: 'SitePlans', params: { siteId: props.siteId }, query: { new: '1' } }, action: 'New plan', retry: admin.loadPlans },
	{ id: 'theme', label: 'Customize the theme', hint: 'Logo, colours and font.', done: themeCustomized.value, unknown: themeUnknown.value, to: { name: 'SiteTheme', params: { siteId: props.siteId }, query: {} }, action: 'Open theme', retry: loadSettings },
	{ id: 'publish', label: 'Publish', hint: 'Make the site public at its address.', done: isPublished.value, unknown: false, action: 'Publish site', run: publish },
])
const checklistDone = computed(() => checklist.value.filter((s) => s.done).length)
const unknownCount = computed(() => checklist.value.filter((s) => s.unknown).length)
const requiredUnknown = computed(() => checklist.value.some((s) => s.unknown && !s.optional))
const requiredLeft = computed(() => checklist.value.filter((s) => !s.done && !s.unknown && !s.optional).length)
// loading → skeleton; incomplete → required work left; unknown → nothing left
// that we know of, but a required step could not be checked; complete.
const checklistState = computed(() => {
	if (checklistLoading.value) return 'loading'
	if (requiredLeft.value > 0) return 'incomplete'
	return requiredUnknown.value ? 'unknown' : 'complete'
})
const setupComplete = computed(() => checklistState.value === 'complete')
const checklistTitle = computed(() => ({ loading: 'Setup checklist', incomplete: 'Set up your site', unknown: 'Setup checklist', complete: 'Setup complete' })[checklistState.value])
const checklistOpen = ref(true)
const retryingStep = ref(null)
const retryStep = async (step) => {
	if (!step.retry || retryingStep.value) return
	retryingStep.value = step.id
	try {
		await step.retry()
	} finally {
		retryingStep.value = null
	}
}
// "Dismiss permanently" is an owner flag castr keeps on the site (§6h:
// `PATCH /sites/{id} { flags: { setupChecklistDismissed } }`, no republish);
// Settings › General has the "Show setup guide" link that clears it.
const checklistDismissed = computed(() => site.value?.flags?.setupChecklistDismissed === true)
const dismissing = ref(false)
const dismissChecklist = async () => {
	if (dismissing.value) return
	dismissing.value = true
	try {
		await store.dispatch('Sites/updateSite', { id: props.siteId, payload: { flags: { setupChecklistDismissed: true } } })
		notify({ group: 'success', text: 'Setup guide hidden. You can bring it back under Settings › General.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not hide the setup guide.' })
	}
	dismissing.value = false
}

// --- revenue ---------------------------------------------------------------------
// castr GET /sites/:id/transactions/summary (§6e) twice: the last 30 days for
// the chart, the lifetime for totals + `recent`. 503 `paywall_site_unconfigured`
// until the site is linked to the owner's Paywall account.
const DAY = 86_400_000
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10)
const rangeTo = isoDay(Date.now())
const rangeFrom = isoDay(Date.now() - 29 * DAY)
const revenue = ref(null)
const revenue30 = ref(null)
const revenueLoading = ref(true)
const revenueError = ref(null)
const revenueUnlinked = computed(() => !site.value?.billing?.paywallSiteId || isPaywallSiteUnconfigured(revenueError.value))
const recent = ref([])
const recentSource = ref(null) // 'summary' | 'list' | null

const loadRevenue = async () => {
	revenueLoading.value = true
	try {
		const [lifetime, month] = await Promise.all([
			SiteAdminService.getTransactionsSummary(props.siteId),
			SiteAdminService.getTransactionsSummary(props.siteId, { from: rangeFrom, to: rangeTo }).catch(() => null),
		])
		revenue.value = lifetime?.summary || null
		revenue30.value = month?.summary || null
		revenueError.value = null
		if (Array.isArray(revenue.value?.recent)) {
			// `recent` is paid-only on castr; filter again so the list and the
			// paid count below never disagree if an older server includes grants.
			recent.value = revenue.value.recent.filter((t) => Number(t.amount) > 0).slice(0, 5)
			recentSource.value = 'summary'
		} else {
			// older castr: no `recent` → newest rows of the list
			const list = await SiteAdminService.listTransactions(props.siteId, { per_page: 5 }).catch(() => null)
			recent.value = (list?.data || []).filter((t) => Number(t.amount) > 0).slice(0, 5)
			recentSource.value = list ? 'list' : null
		}
	} catch (err) {
		revenue.value = null
		revenue30.value = null
		recent.value = []
		revenueError.value = err
	}
	revenueLoading.value = false
}
const series30 = computed(() => (Array.isArray(revenue30.value?.series) ? revenue30.value.series : null))
// One source of truth for "how many purchases": the lifetime summary, paid
// only (`count` includes free grants / 100 % promo redemptions, `recent`
// does not — showing both unfiltered is what made the card contradict itself).
const paidCount = computed(() => paidPurchaseCount(revenue.value))
const paidCount30 = computed(() => paidPurchaseCount(revenue30.value))
const freeCount = computed(() => Number(revenue.value?.free_count) || 0)
const noPurchasesCopy = computed(() => (freeCount.value > 0 && paidCount.value === 0
	? `No paid purchases yet — ${freeCount.value} free ${freeCount.value === 1 ? 'grant' : 'grants'} so far. Paid purchases show up here as soon as a viewer buys a plan.`
	: 'No purchases yet. They show up here as soon as a viewer buys a plan.'))
const lifetimeNet = computed(() => {
	const s = revenue.value
	if (!s) return '—'
	if (s.currency === 'mixed') return Object.entries(s.by_currency || {}).map(([c, v]) => formatMoney(v.net, c)).join(' · ')
	return formatMoney(s.net, s.currency)
})
const txName = (tx) => tx.viewer?.name || tx.viewer?.email || 'Viewer'
const txProduct = (tx) => productTitle(tx, { content: admin.content.value, categories: admin.categories.value }, 'Plan')
const txDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '')

onMounted(async () => {
	loadRevenue()
	checklistLoading.value = true
	// the loaders record their errors (useSiteAdmin `errors[key]`) rather than
	// throw; the skeleton stays until every one of them has answered either way
	await Promise.allSettled([admin.loadContent(), admin.loadPlans(), admin.loadMonetization(), loadSettings()])
	checklistLoading.value = false
	checklistOpen.value = !setupComplete.value
})
</script>

<template>
	<div class="flex flex-col gap-4">
		<Alert v-if="publishFailed" type="error" class="!mb-0 mx-4 lg:mx-0">
			<div class="flex flex-wrap items-center gap-3">
				<span class="flex-1 min-w-48">
					The last publish to the edge failed{{ site.publish?.error ? `: ${site.publish.error}` : '.' }}
					Your changes are saved; retry to push them live.
				</span>
				<FormButton size="sm" type="secondary" :is-loading="publishing" :disabled="publishing" @click="publish">
					Retry publish
				</FormButton>
			</div>
		</Alert>
		<Alert v-else-if="paywallSyncFailed" type="error" class="!mb-0 mx-4 lg:mx-0">
			<div class="flex flex-wrap items-center gap-3">
				<span class="flex-1 min-w-48">
					The site could not be linked to your Paywall account{{ site.paywallSync?.error ? `: ${site.paywallSync.error}` : '.' }}
					Viewer accounts and purchases stay unavailable until this succeeds.
				</span>
				<FormButton size="sm" type="secondary" :is-loading="publishing" :disabled="publishing" @click="publish">
					Retry
				</FormButton>
			</div>
		</Alert>

		<!--
			One column until the content area is really wide (2xl): the app sidebar
			and the site rail leave ~800 px at 1280, too little for two columns.
			At 2xl the checklist and address move beside the status / revenue cards.
		-->
		<div class="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] 2xl:items-start">
				<!-- Status + primary action -->
				<section v-if="isDraftSite" class="rounded-lg bg-surface-2 p-4 ring-1 ring-main/40 sm:p-5 2xl:col-start-1" data-testid="draft-site-card">
					<div class="flex flex-wrap items-start gap-4">
						<div class="min-w-[16rem] flex-1">
							<h2 class="mb-1 text-headline font-semibold text-white">Your site isn't public yet</h2>
							<p class="mb-0 text-tiny text-surface-7">
								Set it up at your own pace — nobody can see <span class="text-surface-8">{{ site.primaryHost }}</span> until you publish. The preview shows exactly what viewers will get.
							</p>
							<p v-if="addressPending" class="mb-0 mt-2 text-tiny text-surface-8" data-testid="overview-address-pending">
								Your site address is still being set up (usually 1–2 minutes). Preview opens as soon as it is ready.
							</p>
						</div>
						<div class="flex shrink-0 flex-wrap items-center gap-2">
							<FormButton type="secondary" size="md" class="gap-1.5" :is-loading="previewing" :disabled="previewing || addressPending" :title="addressPending ? lockedTitle : undefined" data-testid="overview-preview" @click="openPreview">
								<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-eye-outline /></icon-base>
								Preview
							</FormButton>
							<FormButton type="main" size="md" :is-loading="publishing" :disabled="publishing" data-testid="overview-publish" @click="publish">Publish site</FormButton>
						</div>
					</div>
				</section>

				<section v-else class="rounded-lg bg-surface-2 p-4 sm:p-5 2xl:col-start-1" data-testid="status-card">
					<div class="flex flex-wrap items-start gap-4">
						<div class="min-w-[16rem] flex-1">
							<FormSwitch id="site-enabled" :model-value="isEnabled" :disabled="togglingStatus"
								:label="isEnabled ? (addressPending ? (readiness.failed.value ? 'Site address not ready' : 'Site is being set up') : 'Site is live') : 'Site is disabled'"
								@update:model-value="onStatusToggle" />
							<p v-if="addressPending && isEnabled" class="mb-0 mt-1 text-tiny text-surface-8" data-testid="overview-address-pending">
								<template v-if="readiness.failed.value">The site address could not be set up — see the notice above. Viewers cannot open the site until it is fixed.</template>
								<template v-else>Your site address is still being set up (usually 1–2 minutes). Viewers can open it as soon as it is ready; you can keep adding content meanwhile.</template>
							</p>
							<p v-else class="mb-0 mt-1 text-tiny text-surface-7" data-testid="status-propagation-hint">Changes can take up to a minute to reach every region.</p>
							<p class="mb-0 mt-2 text-tiny text-surface-7">
								<template v-if="publishSkipped">Edge publishing is not configured for this environment.</template>
								<template v-else-if="site.publish?.at">Last published {{ new Date(site.publish.at).toLocaleString() }}.</template>
								<template v-else>Not published yet.</template>
								<template v-if="hasDraft"> <span class="text-white">{{ draftCount }} unpublished {{ draftCount === 1 ? 'change' : 'changes' }}</span> waiting.</template>
							</p>
						</div>
						<div class="flex shrink-0 flex-wrap items-center gap-2">
							<FormButton type="secondary" size="md" class="gap-1.5" :is-loading="previewing" :disabled="previewing || addressPending" :title="addressPending ? lockedTitle : undefined" data-testid="overview-preview" @click="openPreview">
								<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-eye-outline /></icon-base>
								Preview
							</FormButton>
							<FormButton v-if="hasDraft" type="main" size="md" :is-loading="publishing" :disabled="publishing" data-testid="overview-publish" @click="publish">Publish changes</FormButton>
							<FormButton v-else type="secondary" size="md" class="gap-1.5" :disabled="!isEnabled || addressPending" :title="addressPending ? lockedTitle : undefined" data-testid="overview-open-site" @click="openSite">
								<icon-base :width="16" :height="16" fill="none"><icon-link-external /></icon-base>
								View site
							</FormButton>
						</div>
					</div>
				</section>

				<!-- Setup checklist (beside the status card at 2xl, right below it otherwise) -->
				<section v-if="!checklistDismissed" class="rounded-lg bg-surface-2 p-4 sm:p-5 2xl:col-start-2 2xl:row-start-1 2xl:row-span-2" data-testid="setup-checklist" :data-state="checklistState" :aria-busy="checklistLoading || dismissing">
					<div class="flex items-start justify-between gap-3">
						<div class="min-w-0">
							<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">{{ checklistTitle }}</h2>
							<p v-if="checklistLoading" class="mb-0 text-tiny text-surface-7" data-testid="checklist-loading">Checking your setup…</p>
							<p v-else class="mb-0 text-tiny text-surface-7" data-testid="checklist-summary">
								{{ checklistDone }} of {{ checklist.length }} done<template v-if="requiredLeft > 0"> · {{ requiredLeft }} required {{ requiredLeft === 1 ? 'step' : 'steps' }} left</template><template v-if="unknownCount > 0"> · {{ unknownCount }} couldn't be checked</template>
							</p>
						</div>
						<div class="flex shrink-0 items-center gap-3 text-tiny">
							<button type="button" class="text-surface-7 underline hover:text-white disabled:opacity-50" :disabled="checklistLoading" data-testid="checklist-toggle" @click="checklistOpen = !checklistOpen">{{ checklistOpen ? 'Hide' : 'Show' }}</button>
							<button type="button" class="text-surface-7 underline hover:text-white disabled:opacity-50" :disabled="dismissing" title="Hide this guide for good — Settings › General brings it back" data-testid="checklist-dismiss" @click="dismissChecklist">Dismiss permanently</button>
						</div>
					</div>
					<div class="mt-3 h-1 w-full overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
						<div v-if="checklistLoading" class="h-full w-full animate-pulse rounded-full bg-surface-4"></div>
						<div v-else class="h-full rounded-full bg-main transition-all" :style="{ width: `${Math.round((checklistDone / checklist.length) * 100)}%` }"></div>
					</div>
					<!-- Skeleton rows while loading: nothing is "to do" until we know. -->
					<ol v-if="checklistLoading" class="m-0 mt-3 flex list-none flex-col gap-1 p-0" data-testid="checklist-skeleton" aria-hidden="true">
						<li v-for="n in checklist.length" :key="n" class="flex items-center gap-3 rounded-lg px-2 py-2">
							<span class="size-5 shrink-0 animate-pulse rounded-full bg-surface-4"></span>
							<span class="h-3 animate-pulse rounded bg-surface-4" :class="n % 2 ? 'w-40' : 'w-32'"></span>
						</li>
					</ol>
					<ol v-else-if="checklistOpen" class="m-0 mt-3 flex list-none flex-col gap-1 p-0">
						<li v-for="step in checklist" :key="step.id" class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-2 py-2" :class="step.done ? 'opacity-70' : 'bg-surface-3'" :data-testid="`checklist-${step.id}`" :data-done="step.done" :data-unknown="step.unknown || undefined">
							<span class="grid size-5 shrink-0 place-items-center rounded-full" :class="step.done ? 'bg-success text-white' : step.unknown ? 'border border-dashed border-surface-6 text-transparent' : 'border border-surface-6 text-transparent'" aria-hidden="true">
								<icon-base v-if="step.done" :width="10" :height="10" fill="none"><icon-checkmark /></icon-base>
							</span>
							<div class="min-w-[10rem] flex-1">
								<p class="mb-0 text-sm" :class="step.done ? 'text-surface-8 line-through' : 'text-white'">
									{{ step.label }}<span v-if="step.optional && !step.done" class="ml-1.5 text-tiny text-surface-7">optional</span>
								</p>
								<p v-if="step.unknown" class="mb-0 text-tiny text-surface-7">Couldn't check this step right now.</p>
								<p v-else-if="!step.done" class="mb-0 text-tiny text-surface-7">{{ step.hint }}</p>
							</div>
							<FormButton v-if="step.unknown" type="secondary" size="sm" class="ml-auto shrink-0" :is-loading="retryingStep === step.id" :disabled="retryingStep !== null" :data-testid="`checklist-${step.id}-retry`" @click="retryStep(step)">Retry</FormButton>
							<template v-else-if="!step.done">
								<FormButton v-if="step.run" type="main" size="sm" class="ml-auto shrink-0" :is-loading="publishing" :disabled="publishing" @click="step.run">{{ step.action }}</FormButton>
								<FormButton v-else type="secondary" size="sm" class="ml-auto shrink-0" :to="step.to">{{ step.action }}</FormButton>
							</template>
						</li>
					</ol>
				</section>

				<!-- Broadcast template: native donations summary; nothing while giving is off -->
				<BroadcastGivingSummaryCard v-if="isBroadcast" :site-id="siteId" class="2xl:col-start-1" />

				<!-- Revenue trend (last 30 days) -->
				<section class="rounded-lg bg-surface-2 p-4 sm:p-5 2xl:col-start-1" data-testid="revenue-card">
					<div class="mb-2 flex flex-wrap items-start justify-between gap-2">
						<div class="min-w-0">
							<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Revenue · last 30 days</h2>
							<p class="mb-0 text-tiny text-surface-7">Net of refunds, by day. Details and refunds under Transactions.</p>
						</div>
						<FormButton type="secondary" size="sm" class="shrink-0" :to="{ name: 'SiteTransactions', params: { siteId } }">View transactions</FormButton>
					</div>
					<p v-if="revenueUnlinked" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="revenue-unlinked">
						Transactions appear once the site is linked to your Paywall account<template v-if="paywallSyncFailed"> — retry the publish above</template>. Nothing has been sold yet.
					</p>
					<Alert v-else-if="revenueError && !revenueLoading" type="error" class="!mb-0">{{ revenueError.message || 'Could not load the revenue summary.' }}</Alert>
					<template v-else>
						<SiteRevenueChart :series="series30 || []" :purchases="paidCount30" :bucket="revenue30?.bucket || 'day'" :currency="revenue30?.currency || revenue?.currency || 'USD'" :height="160" :loading="revenueLoading">
							<template #empty>
								<span v-if="series30 === null && !revenueLoading" data-testid="revenue-series-missing">Daily totals are not available from the server yet.</span>
								<span v-else-if="paidCount === 0" data-testid="revenue-chart-no-purchases">{{ noPurchasesCopy }}</span>
								<span v-else>No revenue in the last 30 days.</span>
							</template>
						</SiteRevenueChart>
						<dl class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="revenue-totals">
							<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
								<dt class="mb-0.5 truncate text-tiny text-surface-7">All-time net</dt>
								<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="revenue-net">{{ lifetimeNet }}</dd>
							</div>
							<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
								<dt class="mb-0.5 truncate text-tiny text-surface-7">Purchases</dt>
								<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="revenue-count">{{ paidCount ?? revenue?.count ?? '—' }}</dd>
								<p v-if="freeCount > 0" class="mb-0 truncate text-[11px] text-surface-7" data-testid="revenue-free-count">+ {{ freeCount }} free {{ freeCount === 1 ? 'grant' : 'grants' }}</p>
							</div>
							<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
								<dt class="mb-0.5 truncate text-tiny text-surface-7">Refunded</dt>
								<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="revenue-refunded">{{ revenue ? formatMoney(revenue.refunded, revenue.currency === 'mixed' ? undefined : revenue.currency) : '—' }}</dd>
							</div>
						</dl>
					</template>
				</section>

				<!-- Recent purchases -->
				<section v-if="!revenueUnlinked" class="rounded-lg bg-surface-2 p-4 sm:p-5 2xl:col-start-1" data-testid="recent-purchases">
					<h2 class="mb-2 text-headline font-semibold text-surface-text-2">Recent purchases</h2>
					<p v-if="!revenueLoading && !recent.length" class="mb-0 text-tiny text-surface-7" data-testid="recent-purchases-empty">{{ noPurchasesCopy }}</p>
					<ul v-else class="m-0 flex list-none flex-col divide-y divide-surface-3 p-0">
						<li v-for="tx in recent" :key="tx.public_id || tx.id" class="flex items-center gap-3 py-2 first:pt-0 last:pb-0" :data-testid="`recent-${tx.public_id || tx.id}`">
							<div class="min-w-0 flex-1">
								<p class="mb-0 truncate text-sm text-white">{{ txName(tx) }}</p>
								<p class="mb-0 truncate text-tiny text-surface-7">{{ txProduct(tx) }} · {{ txDate(tx.created_at) }}</p>
							</div>
							<StatusPill v-if="tx.status && tx.status !== 'active'" pill size="sm" :variant="TX_STATUS_VARIANTS[tx.status] === 'danger' ? 'warning' : TX_STATUS_VARIANTS[tx.status]">{{ TX_STATUS_LABELS[tx.status] || tx.status }}</StatusPill>
							<span class="shrink-0 text-sm font-medium text-white">{{ formatMoney(tx.net_amount ?? tx.amount, tx.currency) }}</span>
						</li>
					</ul>
				</section>

				<!--
					Address: under the checklist in the right column. Once the checklist is
					dismissed it takes the checklist's place at the top of that column —
					auto-placement alone would leave it beside the last left-hand card with
					two empty rows above it (Trello QkQOVww1).
				-->
				<section class="rounded-lg bg-surface-2 p-4 sm:p-5 2xl:col-start-2" :class="checklistDismissed ? '2xl:row-start-1' : ''" data-testid="address-card">
					<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Address</h2>
					<p class="mb-0 flex items-center gap-2 text-sm text-white">
						<span class="min-w-0 truncate" :title="site.url">{{ site.canonicalHost ? `https://${site.canonicalHost}` : site.url }}</span>
						<StatusPill v-if="addressPending" pill size="sm" :variant="readiness.failed.value ? 'warning' : 'accent'" :pulse="!readiness.failed.value" data-testid="overview-address-status">
							{{ readiness.failed.value ? 'Not ready' : 'Setting up' }}
						</StatusPill>
					</p>
					<p class="mb-0 mt-1 text-tiny text-surface-7">
						<router-link :to="{ name: 'SiteDomains', params: { siteId } }" class="underline">Domains</router-link><SitesPlanLockBadge v-if="customDomainLocked" class="ml-1.5" /> ·
						<router-link :to="{ name: 'SiteGeneral', params: { siteId } }" class="underline">General settings</router-link>
					</p>
				</section>
		</div>
	</div>
</template>
